/*!
 * Brooklyn theme.Slideshow Swiper bridge (ES module).
 * Vendored Swiper 14.3.0 ESM; no jQuery. Exposes window.themeSlideshowSwiper
 * for theme.SlideshowSection (theme-main.js).
 * UX parity with prior Slick theme.Slideshow: fade, arrows + dots, autoplay
 * (section setting), custom pause button + scroll/focus pause, no drag,
 * rewind wrap, keyboard, hero--color-N, a11y labels.
 */
import Swiper from './swiper-bundle.esm.min.js';

const CLASSES = {
  heroSlideHidden: 'hero__slide--hidden',
  isPaused: 'is-paused',
  colorPrefix: 'hero--color-',
};

const SELECTORS = {
  pause: '[data-pause]',
  slidePrevious: '[data-slide-previous]',
  slideNext: '[data-slide-next]',
  dotsWrapper: '[data-hero-dots-wrapper]',
  slides: ':scope > .swiper-wrapper > .swiper-slide',
};

function escapeAttr(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function reducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function isTouchDevice() {
  return (
    document.documentElement.classList.contains('supports-touch') ||
    ('ontouchstart' in window && navigator.maxTouchPoints > 0)
  );
}

function parseAutoplayEnabled(root) {
  const raw = root.getAttribute('data-autoplay');
  return raw === true || raw === 'true';
}

function parseAutoplaySpeed(root) {
  const parsed = Number.parseInt(root.getAttribute('data-autoplayspeed'), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 5000;
}

function debounce(fn, wait) {
  let timer = 0;
  return function debounced(...args) {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn.apply(this, args), wait);
  };
}

function slideCount(root) {
  return root.querySelectorAll(SELECTORS.slides).length;
}

function applySlideColor(root, nextIndex, previousIndex) {
  if (typeof previousIndex === 'number' && previousIndex >= 0) {
    root.classList.remove(CLASSES.colorPrefix + previousIndex);
  }
  if (typeof nextIndex === 'number' && nextIndex >= 0) {
    root.classList.add(CLASSES.colorPrefix + nextIndex);
  }
}

function updatePaginationLabels(root, activeIndex) {
  const loadLabel = root.getAttribute('data-slide-nav-a11y') || '';
  const activeLabel = root.getAttribute('data-slide-nav-active-a11y') || '';
  const bullets = root.querySelectorAll('[data-slide-pagination]');
  bullets.forEach((bullet, index) => {
    const template = index === activeIndex ? activeLabel : loadLabel;
    if (!template) return;
    bullet.setAttribute('aria-label', template.replace('[slide_number]', String(index + 1)));
  });
}

function setNonTouchVisibility(instance, isTouch) {
  if (isTouch) return;
  instance.slides.forEach((slide) => {
    slide.classList.add(CLASSES.heroSlideHidden);
    slide.setAttribute('aria-hidden', 'true');
  });
  const active = instance.slides[instance.activeIndex];
  if (active) {
    active.classList.remove(CLASSES.heroSlideHidden);
    active.setAttribute('aria-hidden', 'false');
  }
}

function togglePauseAttributes(pauseButton, currentlyPaused) {
  if (!pauseButton) return;
  pauseButton.classList.toggle(CLASSES.isPaused, !currentlyPaused);
  const label = currentlyPaused
    ? pauseButton.getAttribute('data-label-pause')
    : pauseButton.getAttribute('data-label-play');
  if (label) pauseButton.setAttribute('aria-label', label);
}

function userPaused(root) {
  const pauseButton = root.querySelector(SELECTORS.pause);
  return Boolean(pauseButton?.classList.contains(CLASSES.isPaused));
}

function stopAutoplay(root) {
  root.swiper?.autoplay?.stop();
}

function startAutoplay(root) {
  if (!parseAutoplayEnabled(root) || userPaused(root) || reducedMotion()) return;
  root.swiper?.autoplay?.start();
}

function bindPauseControls(root) {
  if (!parseAutoplayEnabled(root) || slideCount(root) < 2) return;

  const pauseButton = root.querySelector(SELECTORS.pause);
  if (pauseButton) {
    pauseButton.addEventListener('click', () => {
      const paused = pauseButton.classList.contains(CLASSES.isPaused);
      togglePauseAttributes(pauseButton, paused);
      if (paused) {
        startAutoplay(root);
      } else {
        stopAutoplay(root);
      }
    });
  }

  const onScroll = debounce(() => {
    const rect = root.getBoundingClientRect();
    const slideshowOffsetY = rect.top + window.pageYOffset + root.offsetHeight;
    if (slideshowOffsetY < window.pageYOffset) {
      togglePauseAttributes(pauseButton, false);
      stopAutoplay(root);
    } else if (pauseButton && !pauseButton.classList.contains(CLASSES.isPaused)) {
      togglePauseAttributes(pauseButton, true);
      startAutoplay(root);
    }
  }, 250);

  document.addEventListener('scroll', onScroll, { passive: true });
  root._themeSlideshowScrollHandler = onScroll;
}

function bindA11yHoverPause(root) {
  const onEnter = (evt) => {
    if (!root.contains(evt.target) || root.getAttribute('aria-live') === 'polite') {
      return;
    }
    root.setAttribute('aria-live', 'polite');
    if (parseAutoplayEnabled(root)) {
      stopAutoplay(root);
    }
  };

  const onLeave = (evt) => {
    if (evt.relatedTarget && root.contains(evt.relatedTarget)) {
      return;
    }
    root.removeAttribute('aria-live');
    if (parseAutoplayEnabled(root) && !userPaused(root)) {
      startAutoplay(root);
    }
  };

  root.addEventListener('focusin', onEnter);
  root.addEventListener('mouseenter', onEnter);
  root.addEventListener('focusout', onLeave);
  root.addEventListener('mouseleave', onLeave);
  root._themeSlideshowA11yBound = true;
}

/**
 * @param {HTMLElement} root - `#Hero-*` with `.swiper`
 */
function create(root) {
  if (!root) return null;
  if (root.swiper) return root.swiper;

  const slides = root.querySelectorAll(SELECTORS.slides);
  if (slides.length === 0) return null;

  const multi = slides.length > 1;
  const prevEl = root.querySelector(SELECTORS.slidePrevious);
  const nextEl = root.querySelector(SELECTORS.slideNext);
  const paginationEl = root.querySelector(SELECTORS.dotsWrapper);
  const isTouch = isTouchDevice();
  const autoplayOn = parseAutoplayEnabled(root) && multi && !reducedMotion();
  const autoplaySpeed = parseAutoplaySpeed(root);
  const loadSlideA11y = root.getAttribute('data-slide-nav-a11y') || 'Load slide [slide_number]';
  const activeSlideA11y = root.getAttribute('data-slide-nav-active-a11y') || 'Slide [slide_number]';

  let previousIndex = 0;

  const swiper = new Swiper(root, {
    slidesPerView: 1,
    slidesPerGroup: 1,
    effect: 'fade',
    fadeEffect: {
      crossFade: true,
    },
    speed: 500,
    // Prior Slick infinite:true without clones under fade → rewind wrap.
    rewind: multi,
    loop: false,
    allowTouchMove: false,
    watchOverflow: true,
    autoHeight: false,
    grabCursor: false,
    keyboard: {
      enabled: true,
      onlyInViewport: true,
    },
    a11y: {
      enabled: true,
    },
    navigation:
      multi && prevEl && nextEl
        ? {
            nextEl,
            prevEl,
          }
        : undefined,
    pagination:
      multi && paginationEl
        ? {
            el: paginationEl,
            clickable: true,
            type: 'bullets',
            bulletClass: 'swiper-pagination-bullet',
            bulletActiveClass: 'swiper-pagination-bullet-active',
            renderBullet(index, className) {
              const template = index === 0 ? activeSlideA11y : loadSlideA11y;
              const label = escapeAttr(template.replace('[slide_number]', String(index + 1)));
              return `<button type="button" class="${className}" aria-label="${label}" data-slide-pagination data-slide-number="${index}"></button>`;
            },
          }
        : undefined,
    autoplay: autoplayOn
      ? {
          delay: autoplaySpeed,
          disableOnInteraction: false,
          // Custom focus/hover/scroll/pause-button handlers match prior Slick UX
          // (Slick mouseleave.slick was unbound; theme handlers own resume).
          pauseOnMouseEnter: false,
        }
      : false,
    on: {
      init(instance) {
        if (!isTouch) {
          instance.slides.forEach((slide) => {
            slide.classList.add(CLASSES.heroSlideHidden);
          });
          const active = instance.slides[instance.activeIndex];
          active?.classList.remove(CLASSES.heroSlideHidden);
        }

        bindPauseControls(root);
        bindA11yHoverPause(root);
        applySlideColor(root, 0, 1);
        root.setAttribute('data-slide-index', '0');
        updatePaginationLabels(root, 0);
        setNonTouchVisibility(instance, isTouch);
      },
      slideChangeTransitionStart(instance) {
        const nextIndex = instance.realIndex;
        root.setAttribute('data-slide-index', String(nextIndex));

        if (!isTouch) {
          instance.slides.forEach((slide) => {
            slide.classList.remove(CLASSES.heroSlideHidden);
          });
        }

        applySlideColor(root, nextIndex, previousIndex);
        updatePaginationLabels(root, nextIndex);
        previousIndex = nextIndex;
      },
      slideChangeTransitionEnd(instance) {
        setNonTouchVisibility(instance, isTouch);
      },
    },
  });

  requestAnimationFrame(() => {
    if (!root.swiper) return;
    root.swiper.updateSize();
    root.swiper.updateSlides();
  });

  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onMotionChange = (event) => {
    if (!root.swiper) return;
    if (event.matches) {
      stopAutoplay(root);
    } else if (parseAutoplayEnabled(root) && multi && !userPaused(root)) {
      root.swiper.params.autoplay = {
        delay: autoplaySpeed,
        disableOnInteraction: false,
        pauseOnMouseEnter: false,
      };
      startAutoplay(root);
    }
  };
  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', onMotionChange);
  } else if (typeof motionQuery.addListener === 'function') {
    motionQuery.addListener(onMotionChange);
  }
  root._themeSlideshowMotionHandler = onMotionChange;
  root._themeSlideshowMotionQuery = motionQuery;

  return swiper;
}

function destroy(root) {
  if (!root) return;

  if (root._themeSlideshowScrollHandler) {
    document.removeEventListener('scroll', root._themeSlideshowScrollHandler);
    root._themeSlideshowScrollHandler = null;
  }

  const motionQuery = root._themeSlideshowMotionQuery;
  const motionHandler = root._themeSlideshowMotionHandler;
  if (motionQuery && motionHandler) {
    if (typeof motionQuery.removeEventListener === 'function') {
      motionQuery.removeEventListener('change', motionHandler);
    } else if (typeof motionQuery.removeListener === 'function') {
      motionQuery.removeListener(motionHandler);
    }
  }
  root._themeSlideshowMotionHandler = null;
  root._themeSlideshowMotionQuery = null;

  if (root.swiper) {
    root.swiper.destroy(true, true);
  }
}

function slideTo(root, index, speed) {
  if (!root?.swiper || typeof index !== 'number' || index < 0) return;
  root.swiper.slideTo(index, speed);
}

function pause(root) {
  stopAutoplay(root);
}

function play(root) {
  startAutoplay(root);
}

function getSlideIndexForBlock(root, blockId) {
  if (!root || !blockId) return undefined;
  const slide = root.querySelector(`.slide--${CSS.escape ? CSS.escape(blockId) : blockId}`);
  if (!slide || !slide.parentElement) return undefined;
  const index = Array.prototype.indexOf.call(slide.parentElement.children, slide);
  return index < 0 ? undefined : index;
}

window.themeSlideshowSwiper = {
  create,
  destroy,
  slideTo,
  pause,
  play,
  getSlideIndexForBlock,
};

document.dispatchEvent(new CustomEvent('theme:slideshow-swiper-ready'));
